import React, { useContext, useState } from 'react';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import styles from './styles/SituationsAndGroups.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AuthContext from '../../../context/AuthContext';
import { hashStringToColor } from '../../../utils/collabColors';

/**
 * The outline: every situation, grouped or not, with drag-and-drop between
 * groups.
 *
 * Membership is `node.data.situationGroup` — a plain id on the situation — so a
 * drop is one `updateNodeData` op. Nothing writes to the group, because
 * `SituationGroup.situations` is rebuilt from the draft at publish time.
 */
const SituationsAndGroups = ({ setRenderEditSidebar }) => {
    const { nodes, groups, updateSituation, createGroup, centerNodeInView } = useCanvas();
    const { selectItem, selectedNodeId, selectedGroupId, selectedGroupQueue } = useEditorUI();
    const { userId: authUserId } = useContext(AuthContext);

    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [isDragging, setIsDragging] = useState(false);

    const handleCreateGroupClick = () => {
        const newGroupId = createGroup();
        selectItem('group', newGroupId);
        setRenderEditSidebar(true);
    };

    // Membership lives on the node, and a node's group id is a string in the draft
    // while an unsaved server round-trip could hand back an ObjectId, so compare
    // as strings.
    const membersOf = (groupId) =>
        nodes.filter((node) => String(node.data?.situationGroup ?? '') === String(groupId));
    const ungrouped = nodes.filter((node) => !node.data?.situationGroup);

    const onDragEnd = (result) => {
        setIsDragging(false);

        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        updateSituation(draggableId, {
            situationGroup: destination.droppableId === 'ungrouped' ? null : destination.droppableId,
        });
    };

    const onDragUpdate = (update) => {
        if (!update.destination) return;
        const { droppableId } = update.destination;
        // Expand whatever the author is hovering over, so they can see where it lands.
        if (droppableId !== 'ungrouped') {
            setCollapsedGroups((prev) => ({ ...prev, [droppableId]: false }));
        }
    };

    const openSituation = (nodeId) => {
        selectItem('situation', nodeId);
        centerNodeInView(nodeId);
    };

    const renderSituation = (node, index, bulletColor, dragDisabled = false) => (
        <Draggable
            key={node.id}
            draggableId={node.id}
            index={index}
            isDragDisabled={dragDisabled}
        >
            {(provided) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`${styles.situationItem} ${
                        bulletColor !== '#000' ? styles.groupedSituationItem : ''
                    } ${selectedNodeId === node.id ? styles.selectedSituation : ''}`}
                    onClick={() => openSituation(node.id)}
                    style={{
                        '--bullet-color': bulletColor,
                        ...provided.draggableProps.style,
                    }}
                >
                    {node.data?.title || 'Untitled Situation'}
                </div>
            )}
        </Draggable>
    );

    const renderUngrouped = (extraStyle = {}) => (
        <Droppable droppableId="ungrouped">
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${styles.situationList} ${
                        snapshot.isDraggingOver ? styles.isDraggingOver : ''
                    }`}
                    style={{ minHeight: '10px', ...extraStyle }}
                >
                    {ungrouped.map((node, index) => renderSituation(node, index, '#000'))}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );

    return (
        <div className={styles.container}>
            <button className={styles.createGroupButton} onClick={handleCreateGroupClick}>
                Create Group
            </button>

            <DragDropContext
                onDragStart={() => setIsDragging(true)}
                onDragEnd={onDragEnd}
                onDragUpdate={onDragUpdate}
            >
                <div className={styles.list}>
                    {groups.map((group) => {
                        const members = membersOf(group.id);
                        const queue = selectedGroupQueue[group.id];
                        // Somebody else holds this group; dropping into it would
                        // fight their edit.
                        const locked = !!queue && queue[0] !== authUserId;
                        const bulletColor = group.color === '#ffffff' ? '#000' : group.color;

                        return (
                            <div key={group.id}>
                                <div
                                    className={`${styles.groupTitle} ${
                                        selectedGroupId === group.id ? styles.selectedGroup : ''
                                    } ${queue ? styles.highlightGroup : ''}`}
                                    style={{
                                        '--highlight-color': queue
                                            ? hashStringToColor(queue[0])
                                            : undefined,
                                        color: bulletColor,
                                    }}
                                    onClick={() => selectItem('group', group.id)}
                                    onMouseEnter={() => {
                                        if (isDragging) {
                                            setCollapsedGroups((prev) => ({
                                                ...prev,
                                                [group.id]: false,
                                            }));
                                        }
                                    }}
                                >
                                    <span
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setCollapsedGroups((prev) => ({
                                                ...prev,
                                                [group.id]: !prev[group.id],
                                            }));
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                collapsedGroups[group.id]
                                                    ? faCaretRight
                                                    : faCaretDown
                                            }
                                        />
                                    </span>
                                    {group.title || 'Untitled Group'}
                                    {collapsedGroups[group.id] && (
                                        <span>&nbsp;({members.length})</span>
                                    )}
                                </div>

                                <Droppable droppableId={group.id} isDropDisabled={locked}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`${styles.situationList} ${
                                                snapshot.isDraggingOver
                                                    ? styles.isDraggingOver
                                                    : ''
                                            }`}
                                            style={{
                                                display: collapsedGroups[group.id]
                                                    ? 'none'
                                                    : 'block',
                                                minHeight: '10px',
                                            }}
                                        >
                                            {members.map((node, index) =>
                                                renderSituation(node, index, bulletColor, locked)
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        );
                    })}

                    {groups.length > 0 ? (
                        <>
                            <div className={styles.ungroupedTitle}>Ungrouped Situations</div>
                            {renderUngrouped()}
                        </>
                    ) : (
                        renderUngrouped({ paddingLeft: '0' })
                    )}
                </div>
            </DragDropContext>
        </div>
    );
};

export default SituationsAndGroups;
